import { Injectable, OnDestroy } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { filter, Subscription } from "rxjs";

/**Handles the tab title and the favicon notification count*/
@Injectable({
  providedIn: 'root'
})
export class DesktopTitleService implements OnDestroy {

  /**Duration of new message title*/
  public static readonly MESSAGE_DURATION: number = 8000;

  /**The saved base title of the current route*/
  private routeTitle: string = '';

  private timeoutId?: ReturnType<typeof setTimeout>;

  private routeChangeEvent: Subscription;

  private originalFaviconUrl: string | null = null;

  private linkElement: HTMLLinkElement | null = null;

  constructor(
    private title: Title,
    private router: Router,
    private route: ActivatedRoute
  ) {

    this.routeChangeEvent = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      const routeTitle = this.getTitleFromRoute(this.route);
      if (routeTitle) {
        this.routeTitle = routeTitle;
      }
    });

    this.linkElement = document.querySelector("link[rel*='icon']");
    this.originalFaviconUrl = this.linkElement?.href || '/favicon.ico';
  }

  public ngOnDestroy(): void {
    this.routeChangeEvent.unsubscribe();
    clearTimeout(this.timeoutId);
  }

  /**Extract the title string from the route data, finding the deepest matching route that has a title*/
  private getTitleFromRoute(route: ActivatedRoute): string | undefined {
    let child = route;
    
    while (child.firstChild) {
      child = child.firstChild;
    }

    while (child) {
      const title =
        child.snapshot.data['title'] ??
        child.snapshot.routeConfig?.title;

      if (title) {
        return title;
      }

      child = child.parent!;
    }

    return undefined;
  }

  /**Set the tab title temporarly and resume the previous title after a duration*/
  public setTitle(title: string): void {
    this.title.setTitle(title);

    clearTimeout(this.timeoutId);
    this.timeoutId = setTimeout(() => {
      this.title.setTitle(this.routeTitle);
    }, DesktopTitleService.MESSAGE_DURATION);
  }

  private resetFavicon(): void {
    if (this.linkElement && this.originalFaviconUrl) {
      this.linkElement.href = this.originalFaviconUrl;
    }
  }

  /**Change the favicon so it shows a badge with the message count*/
  public setNotificationCount(count: number): void {
    if (!this.linkElement) return;

    if (count <= 0) {
      this.resetFavicon();
      return;
    }

    const img = new Image();
    img.src = this.originalFaviconUrl!;

    img.onload = () => {
      const size = 64;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;

      ctx.drawImage(img, 0, 0, size, size);

      // red circle
      const radius = 20;
      ctx.beginPath();
      ctx.arc(size - radius, radius, radius, 0, 2 * Math.PI);
      ctx.fillStyle = '#e00';
      ctx.fill();

      // count text
      ctx.font = 'bold 28px sans-serif';
      ctx.fillStyle = 'white';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(count > 9 ? '9+' : count+'', size - radius, radius);

      this.applyFavicon(canvas.toDataURL('image/png'));

    };
  }

  private applyFavicon(url: string): void {
    // Remove existing favicons
    const links = document.querySelectorAll("link[rel*='icon']");
    links.forEach(link => link.parentNode?.removeChild(link));

    // Create a new link element
    const newLink = document.createElement('link');
    newLink.rel = 'icon';
    newLink.type = 'image/png';
    newLink.href = url;
    document.head.appendChild(newLink);

    this.linkElement = newLink;

  }


}
